import { AdminModelSelector } from "./AdminModelSelector";
import { Alert, Button, Link } from "@mui/material";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import {
  CarbonListCell,
  ListValue,
} from "portal/components/table/CarbonListCell";
import {
  CursorFetchArgs,
  CursorPageResponse,
  useCursorDataSource,
} from "portal/utils/hooks/useCursorDataSource";
import { DateTime } from "luxon";
import { DEBOUNCE_MS } from "portal/utils/timing";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import { formatList } from "portal/utils/strings";
import {
  getAdminPlantProfilePath,
  getCustomerPlantProfilePath,
  getPlantProfileEditPath,
} from "portal/utils/routing";
import {
  getDefaultPlantProfile,
  getRobotsByActiveProfileId,
  UnsavedPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import {
  GridColDef,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid-premium";
import {
  isIncompleteRosySearch,
  ROSY_SEARCH_MIN_LENGTH,
  toRosySearchQuery,
} from "portal/utils/categoryCollection/search";
import { NoScroll } from "../Page";
import { PlantProfileOverviewDialog } from "./instructions/PlantProfileOverviewDialog";
import { PlantProfileTab, withPlantProfileTabQuery } from "./tabs/tabs";
import {
  PlantProfile as PlantProfileType,
  SavedPlantProfile,
} from "protos/robot_syncer/plant_profile";
import { portalEndpoints } from "portal/state/portalApi";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { ScopedCustomerMissingFeatureFlagAlert } from "portal/components/customers/ScopedCustomerMissingFeatureFlagAlert";
import { SearchField } from "../header/SearchField";
import { SetPlantProfile } from "./SetPlantProfile";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useCanSeePortalGlobals,
  useCanUpdatePlantProfile,
} from "portal/views/plantProfiles/permissions";
import { useDebounce } from "@uidotdev/usehooks";
import { useLazyPopups, useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/AddOutlined";
import React, { FC, useCallback, useMemo, useState } from "react";

const defaultColumn: Partial<GridColDef> = {
  disableColumnMenu: true,
  flex: 1,
};

const ADMIN_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  activeRobots: false,
};
const CUSTOMER_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  activeRobots: true,
};
const LOADING_LIST_VALUES: ListValue[] = [{ id: "loading", name: "" }];
const EMPTY_LIST_VALUES: ListValue[] = [];

type PlantProfileListProps = {
  onCreateProfile: (
    unsavedPlantProfile: UnsavedPlantProfile
  ) => Promise<{ error?: unknown }>;
} & (
  | {
      isAdmin: true;
      customerUuid?: never;
      useLazyListQuery: typeof portalEndpoints.listGlobalPlantProfiles.useLazyQuery;
    }
  | {
      isAdmin?: false;
      customerUuid: string;
      useLazyListQuery: typeof portalEndpoints.listCustomerPlantProfiles.useLazyQuery;
    }
);

const _PlantProfileList: FC<PlantProfileListProps> = (props) => {
  const {
    onCreateProfile,
    useLazyListQuery,
    isAdmin = false,
    customerUuid,
  } = props;
  const { t, i18n } = useTranslation();
  const { isEnabled: includeCustomCategories } = useFeatureFlagForCustomer(
    FeatureFlag.CUSTOM_CATEGORIES,
    customerUuid
  );

  const [searchInput, setSearchInput] = useState<string>("");
  const searchQuery = useDebounce(toRosySearchQuery(searchInput), DEBOUNCE_MS);
  const [getPlantProfiles] = useLazyPopups(useLazyListQuery());
  const { data: robotSummaries, isLoading: isFleetLoading } = useQueryPopups(
    portalEndpoints.listRobots.useQuery(
      customerUuid ? { excludeMetrics: true } : skipToken
    ),
    { errorVariant: "warning" }
  );
  const robotsByActiveProfileId = useMemo(
    () =>
      getRobotsByActiveProfileId(
        robotSummaries,
        customerUuid ?? "",
        i18n.language
      ),
    [robotSummaries, customerUuid, i18n.language]
  );
  const activeRobotListValuesByProfileId = useMemo(() => {
    const valuesByProfileId = new Map<string, ListValue[]>();
    for (const [profileId, robots] of robotsByActiveProfileId) {
      valuesByProfileId.set(
        profileId,
        robots.map((robot) => ({
          id: robot.serial,
          name: robot.displayName,
        }))
      );
    }
    return valuesByProfileId;
  }, [robotsByActiveProfileId]);
  const fetchPlantProfilesPage = useCallback(
    async ({
      cursor,
      limit,
    }: CursorFetchArgs): Promise<CursorPageResponse<SavedPlantProfile>> => {
      const { data } = await getPlantProfiles({
        ...(customerUuid ? { customerUuid } : {}),
        olderCursor: cursor,
        limit,
        search: searchQuery,
        includeCustomCategories: includeCustomCategories ?? false,
      });
      const profiles = data?.plantProfiles ?? [];
      return {
        rows: profiles,
        pageInfo: {
          hasNextPage: Boolean(data?.pagination?.nextOld),
          nextCursor: data?.pagination?.nextOld,
        },
      };
    },
    [getPlantProfiles, customerUuid, searchQuery, includeCustomCategories]
  );
  const { dataGridProps, fetchedRows: fetchedPlantProfileData } =
    useCursorDataSource<SavedPlantProfile>(fetchPlantProfilesPage);

  const canSeeGlobals = useCanSeePortalGlobals();

  const canUpdate = useCanUpdatePlantProfile(isAdmin);

  const navigate = useNavigate();
  // For M1 we specifically need the 5 specific categories to be named in English
  const SHOULD_INTERNATIONALIZE = false;
  const [defaultPlantProfile] = useState<UnsavedPlantProfile>(() =>
    getDefaultPlantProfile(t, SHOULD_INTERNATIONALIZE)
  );

  const getProfilePath = useCallback(
    (id: string): string =>
      isAdmin
        ? getAdminPlantProfilePath(id)
        : getCustomerPlantProfilePath(id, customerUuid),
    [customerUuid, isAdmin]
  );

  const columns: readonly GridColDef<SavedPlantProfile>[] = useMemo(
    () => [
      {
        ...defaultColumn,
        field: "name",
        headerName: t("models.categoryCollectionProfiles.fields.name"),
        valueGetter: (value, { profile }) => profile?.name ?? "",
        renderCell: ({ row: { profile } }) =>
          profile?.id ? (
            <Link
              component={RouterLink}
              to={getProfilePath(profile.id)}
              underline="hover"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {profile.name}
            </Link>
          ) : undefined,
      },
      {
        ...defaultColumn,
        field: "activeRobots",
        sortable: false,
        minWidth: 140,
        headerName: t("components.robots.RobotSummary.active"),
        valueGetter: (_value, row) =>
          formatList(
            t,
            (
              activeRobotListValuesByProfileId.get(row.profile?.id ?? "") ??
              EMPTY_LIST_VALUES
            ).map(({ name }) => name)
          ),
        renderCell: ({ row }) => (
          <CarbonListCell
            loading={isFleetLoading}
            values={
              isFleetLoading
                ? LOADING_LIST_VALUES
                : activeRobotListValuesByProfileId.get(row.profile?.id ?? "") ??
                  EMPTY_LIST_VALUES
            }
          />
        ),
      },
      {
        ...defaultColumn,
        flex: 0.5,
        field: "clientVersionCreateTime",
        headerName: t("models.categoryCollectionProfiles.fields.updatedAt"),
        valueFormatter: (value, { metadata }) =>
          metadata?.clientVersionCreateTime
            ? DateTime.fromJSDate(
                new Date(metadata.clientVersionCreateTime)
              ).toLocaleString(
                { ...DateTime.TIME_SIMPLE, ...DateTime.DATE_FULL },
                { locale: i18n.language }
              )
            : undefined,
      },
    ],
    [getProfilePath, t, i18n, activeRobotListValuesByProfileId, isFleetLoading]
  );

  return (
    <NoScroll>
      {!isAdmin && (
        <ScopedCustomerMissingFeatureFlagAlert
          flag={FeatureFlag.CATEGORY_COLLECTION}
        />
      )}
      {isAdmin && canUpdate && (
        <Alert severity="warning" className="mb-8">
          {t("components.categoryCollectionProfile.warnings.adminMeta")}
        </Alert>
      )}
      <div className="flex flex-col h-full gap-8">
        {isAdmin && canSeeGlobals && <AdminModelSelector />}
        <CarbonDataGrid<SavedPlantProfile>
          {...dataGridProps}
          key={customerUuid ?? "admin"}
          header={
            <>
              <SearchField
                value={searchInput}
                onChange={setSearchInput}
                helperText={
                  isIncompleteRosySearch(searchInput)
                    ? t("components.header.search.minLength", {
                        count: ROSY_SEARCH_MIN_LENGTH,
                      })
                    : undefined
                }
                label={t("utils.actions.searchLong", {
                  subject: t(
                    "models.categoryCollectionProfiles.categoryCollectionProfile_title_other",
                    {
                      count: fetchedPlantProfileData.length,
                    }
                  ),
                })}
              />
              <div className="flex gap-2">
                <PlantProfileOverviewDialog />
                {canUpdate && (
                  <SetPlantProfile
                    triggerButton={(onClick) => (
                      <Button
                        color="inherit"
                        startIcon={<AddIcon />}
                        className="leading-[0]"
                        onClick={onClick}
                      >
                        <span className="hidden sm:inline">
                          {t("utils.actions.newLong", {
                            subject: t(
                              "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
                            ),
                          })}
                        </span>
                        <span className="sm:hidden">
                          {t("utils.actions.new")}
                        </span>
                      </Button>
                    )}
                    hotkey={{
                      sequence: "n",
                      key: "NEW_PLANT_PROFILE",
                    }}
                    title={t("utils.actions.newLong", {
                      subject: t(
                        "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
                      ),
                    })}
                    okText={t("utils.actions.create")}
                    okIcon={<AddIcon />}
                    nameOnly={includeCustomCategories ?? false}
                    initialValue={{ ...defaultPlantProfile }}
                    customerUuid={customerUuid}
                    onSubmit={async (unsavedPlantProfile) => {
                      const result = await onCreateProfile(unsavedPlantProfile);
                      if (!result.error) {
                        const viewPath = getProfilePath(unsavedPlantProfile.id);
                        if (includeCustomCategories) {
                          navigate(
                            withPlantProfileTabQuery(
                              getPlantProfileEditPath(viewPath),
                              PlantProfileTab.CATEGORIZED
                            )
                          );
                        } else {
                          navigate(viewPath);
                        }
                      }
                    }}
                  />
                )}
              </div>
            </>
          }
          className="flex flex-1"
          columns={columns}
          columnVisibilityModel={
            isAdmin ? ADMIN_COLUMN_VISIBILITY : CUSTOMER_COLUMN_VISIBILITY
          }
          getRowId={({ profile }) => (profile as PlantProfileType).id}
          onRowClick={({ row: { profile } }) =>
            navigate(getProfilePath(profile.id))
          }
          disableRowSelectionOnClick
        />
      </div>
    </NoScroll>
  );
};

export const PlantProfileList = _PlantProfileList;
